/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { swapFormArrayControls } from '@common/tech-shared';
import { InteractivityChecker } from '@angular/cdk/a11y';
import { Component, ElementRef, inject, input, output, ViewChild } from '@angular/core';
import { FormArray } from '@angular/forms';
import { ButtonComponent } from '../../../button/button.component';
import { DropdownMenuButtonItemComponent } from '../../../dropdown-menu/dropdown-menu-button-item/dropdown-menu-button-item.component';
import { DropdownMenuComponent } from '../../../dropdown-menu/dropdown-menu/dropdown-menu.component';
import { FocusUtil } from '../../../focus.util';
import { IconComponent } from '../../../icon/icon.component';
import { TooltipDirective } from '../../../tooltip/tooltip.directive';

@Component({
  selector: 'ods-sub-form-list-item',
  imports: [ButtonComponent, IconComponent, DropdownMenuComponent, DropdownMenuButtonItemComponent, TooltipDirective],
  host: {
    class: 'bg-background-150 flex w-full flex-row items-start justify-between gap-4 rounded-md p-4',
    '[attr.aria-label]': 'regionLabel()',
    role: 'region',
    tabindex: '-1',
  },
  template: `
    <div class="flex flex-1 flex-col gap-4">
      <ng-content />
    </div>
    @if (formArray().length > 1 || canRemoveLast()) {
      @switch (actionsVariant()) {
        @case ('delete') {
          <ods-button
            data-test-id="remove-button"
            variant="ghost"
            size="fit"
            destructive="true"
            [tooltip]="removeButtonLabel() + regionLabel()"
            tooltipAriaType="aria-label"
            (clicked)="remove()"
          >
            <ods-icon icon name="delete" fill="error" />
          </ods-button>
        }
        @case ('menu') {
          <ods-dropdown-menu #dropdownMenu [buttonTooltip]="'Aktionen für ' + regionLabel()" buttonVariant="dark-neutral">
            <ods-icon button-content name="more" fill="neutral" />
            <ods-dropdown-menu-button-item caption="Löschen" (clicked)="remove()" />
            @if (index() > 0) {
              <ods-dropdown-menu-button-item caption="Nach oben bewegen" (clicked)="moveUp()" />
            }
            @if (index() < formArray().length - 1) {
              <ods-dropdown-menu-button-item caption="Nach unten bewegen" (clicked)="moveDown()" />
            }
          </ods-dropdown-menu>
        }
      }
    }
  `,
})
export class SubFormListItemComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly interactivityChecker = inject(InteractivityChecker);

  @ViewChild('dropdownMenu') dropdownMenu: DropdownMenuComponent;

  public readonly formArray = input.required<FormArray>();
  public readonly index = input.required<number>();
  public readonly regionLabel = input.required<string>();
  public readonly actionsVariant = input<'delete' | 'menu'>('delete');
  public readonly removeButtonLabel = input<string>('Entfernen von ');
  public readonly canRemoveLast = input<boolean>(false);

  public readonly removed = output<void>();

  public remove(): void {
    this.formArray().removeAt(this.index());
    this.removed.emit();
  }

  public moveUp(): void {
    if (this.index() === 0) return;
    swapFormArrayControls(this.formArray(), this.index(), this.index() - 1);
    this.dropdownMenu?.close();
  }

  public moveDown(): void {
    if (this.index() === this.formArray().length - 1) return;
    swapFormArrayControls(this.formArray(), this.index(), this.index() + 1);
    this.dropdownMenu?.close();
  }

  public focus(): void {
    this.elementRef.nativeElement.focus();
  }

  public focusFirstField(): void {
    FocusUtil.getTabbableElement(this.elementRef.nativeElement, this.interactivityChecker)?.focus();
  }
}
